import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Configure axios instance for X Search service
const xServiceClient = axios.create({
  baseURL: config.api.xServiceUrl,
  timeout: 30000,
  headers: {}
});

// Add request interceptor to include auth token
xServiceClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(xServiceClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(xServiceClient);

export const xService = {
  // Health check
  healthCheck: async () => {
    const response = await xServiceClient.get('/x/health');
    return response.data;
  },

  // MS Teams channels
  getMSTeamsChannels: async () => {
    const response = await xServiceClient.get('/x/ms-teams-channels');
    return response.data;
  },

  // Search configurations
  getAllSearches: async () => {
    const response = await xServiceClient.get('/x/searches');
    return response.data;
  },

  getSearch: async (searchId) => {
    const response = await xServiceClient.get(`/x/searches/${searchId}`);
    return response.data;
  },

  createSearch: async (data) => {
    const response = await xServiceClient.post('/x/searches', data);
    return response.data;
  },

  updateSearch: async (searchId, data) => {
    const response = await xServiceClient.put(`/x/searches/${searchId}`, data);
    return response.data;
  },

  deleteSearch: async (searchId) => {
    const response = await xServiceClient.delete(`/x/searches/${searchId}`);
    return response.data;
  },

  // Search execution
  runSearch: async (searchId) => {
    const response = await xServiceClient.post(`/x/searches/${searchId}/run`);
    return response.data;
  },

  // Search results
  getRecentResults: async (searchId = null, limit = 10) => {
    const params = { limit };
    if (searchId) {
      params.search_id = searchId;
    }
    const response = await xServiceClient.get('/x/results', { params });
    return response.data;
  },

  // Get search history
  getSearchHistory: async (searchId, limit = 50) => {
    const response = await xServiceClient.get(`/x/searches/${searchId}/history`, {
      params: { limit }
    });
    return response.data;
  },

  // Get specific result by ID
  getResultById: async (resultId) => {
    const response = await xServiceClient.get(`/x/results/${resultId}`);
    return response.data;
  },

  // Analysis operations
  analyzeResult: async (resultId) => {
    const response = await xServiceClient.post(`/x/results/${resultId}/analyze`);
    return response.data;
  },

  getAnalysisStatus: async (resultId, jobId) => {
    const response = await xServiceClient.get(`/x/results/${resultId}/analyze/${jobId}`);
    return response.data;
  },

  // Custom analysis operations
  analyzeCustomResults: async (searchId, resultIds, systemPrompt = null, userPrompt = null) => {
    const response = await xServiceClient.post(`/x/searches/${searchId}/analyze/custom`, {
      result_ids: resultIds,
      system_prompt: systemPrompt,
      user_prompt: userPrompt
    });
    return response.data;
  },

  getCustomAnalysisStatus: async (searchId, jobId) => {
    const response = await xServiceClient.get(`/x/searches/${searchId}/analyze/custom/${jobId}`);
    return response.data;
  },

  // Schedule management
  createSchedule: async (searchId, scheduleData) => {
    const response = await xServiceClient.post(`/x/searches/${searchId}/schedules`, scheduleData);
    return response.data;
  },

  updateSchedule: async (searchId, scheduleId, scheduleData) => {
    const response = await xServiceClient.put(`/x/searches/${searchId}/schedules/${scheduleId}`, scheduleData);
    return response.data;
  },

  deleteSchedule: async (searchId, scheduleId) => {
    const response = await xServiceClient.delete(`/x/searches/${searchId}/schedules/${scheduleId}`);
    return response.data;
  },

  // Notification management
  getAllNotifications: async () => {
    const response = await xServiceClient.get('/x/notifications');
    return response.data;
  },

  getNotification: async (notificationId) => {
    const response = await xServiceClient.get(`/x/notifications/${notificationId}`);
    return response.data;
  },

  createNotification: async (data) => {
    const response = await xServiceClient.post('/x/notifications', data);
    return response.data;
  },

  updateNotification: async (notificationId, data) => {
    const response = await xServiceClient.put(`/x/notifications/${notificationId}`, data);
    return response.data;
  },

  deleteNotification: async (notificationId) => {
    const response = await xServiceClient.delete(`/x/notifications/${notificationId}`);
    return response.data;
  },

  // Group management
  getAllGroups: async () => {
    const response = await xServiceClient.get('/x/groups');
    return response.data;
  },

  getGroup: async (groupId) => {
    const response = await xServiceClient.get(`/x/groups/${groupId}`);
    return response.data;
  },

  createGroup: async (data) => {
    const response = await xServiceClient.post('/x/groups', data);
    return response.data;
  },

  updateGroup: async (groupId, data) => {
    const response = await xServiceClient.put(`/x/groups/${groupId}`, data);
    return response.data;
  },

  deleteGroup: async (groupId) => {
    const response = await xServiceClient.delete(`/x/groups/${groupId}`);
    return response.data;
  },

  // ── Company Research: monitored Official/Trusted X accounts ──────────────
  getResearchAccounts: async (companyId) => {
    const response = await xServiceClient.get(
      `/x/research/companies/${companyId}/accounts`,
    );
    return response.data;
  },

  syncResearchAccounts: async (
    companyId,
    { officialHandles = [], trustedHandles = [], trustedRequiredKeywords } = {},
  ) => {
    const payload = {
      official_handles: officialHandles,
      trusted_handles: trustedHandles,
    };

    if (Array.isArray(trustedRequiredKeywords)) {
      payload.trusted_required_keywords = trustedRequiredKeywords;
    }

    const response = await xServiceClient.post(
      `/x/research/companies/${companyId}/accounts`,
      payload,
    );
    return response.data;
  },

  backfillResearchPosts: async (companyId, { years = 2, maxTweetsPerAccount = 2000 } = {}) => {
    const response = await xServiceClient.post(
      `/x/research/companies/${companyId}/backfill`,
      { years, max_tweets_per_account: maxTweetsPerAccount },
    );
    return response.data;
  },

  backfillResearchAccount: async (companyId, accountId, { years = 2, maxTweetsPerAccount = 2000 } = {}) => {
    const safeAccountId = encodeURIComponent(accountId);
    const response = await xServiceClient.post(
      `/x/research/companies/${companyId}/accounts/${safeAccountId}/backfill`,
      { years, max_tweets_per_account: maxTweetsPerAccount },
    );
    return response.data;
  }
};

export default xService;
